<template>
  <b-modal id="modalCompararModelos" size="xl" hide-footer centered header-class="comparar-modelos-header">
    <template #modal-header="{ close }">
      <div class="comparar-header-content">
        <div class="comparar-header-title">
          <b-icon icon="arrow-left-right" class="comparar-header-icon" />
          Comparação de Modelos XML
        </div>
        <button class="comparar-header-close" @click="close()">✕</button>
      </div>
    </template>

    <div class="comparar-body">
      <!-- Tela de upload -->
      <div v-if="!report" class="comparar-upload-area">
        <div class="comparar-upload-intro">
          <h2 class="comparar-upload-title">Selecione os modelos para comparação</h2>
          <p class="comparar-upload-subtitle">Faça o upload dos arquivos XML do modelo PAP e do modelo RTO.</p>
        </div>
        <div class="comparar-upload-cards">
          <div v-for="slot in slots" :key="slot.key" class="comparar-upload-card" :class="{ 'card-filled': slot.file && !slot.error, 'card-error': slot.error }">
            <b-icon
              :icon="slot.error ? 'x-circle' : (slot.file ? 'check-circle' : 'file-earmark-code')"
              :class="slot.error ? 'card-icon-error' : (slot.file ? 'card-icon-ok' : 'card-icon-empty')"
              font-scale="3"
            />
            <h3 class="card-label">Modelo {{ slot.label }}</h3>
            <p class="card-filename" :class="{ 'card-filename-error': slot.error }">
              {{ slot.error ? slot.error : (slot.file ? slot.file.name : 'Selecione o arquivo XML') }}
            </p>
            <input type="file" :ref="'fileInput_' + slot.key" accept=".xml" hidden @change="onFile($event, slot.key)" />
            <b-button size="sm" :variant="slot.error ? 'danger' : (slot.file ? 'success' : 'outline-secondary')" @click="$refs['fileInput_' + slot.key][0].click()">
              {{ slot.file || slot.error ? 'Trocar Arquivo' : 'Procurar Arquivo' }}
            </b-button>
          </div>
        </div>
        <div class="comparar-upload-action">
          <b-button
            class="btn-comparar"
            :disabled="!slots[0].file || !slots[1].file || loading"
            @click="executar"
          >
            <b-spinner v-if="loading" small class="mr-2" />
            <b-icon v-else icon="arrow-left-right" class="mr-2" />
            {{ loading ? 'Analisando...' : 'Comparar Modelos' }}
          </b-button>
        </div>
      </div>

      <!-- Relatório -->
      <div v-else class="comparar-report">
        <div class="comparar-report-header">
          <h2 class="comparar-report-title">Relatório de Comparação</h2>
          <b-button variant="link" class="btn-nova-comparacao" @click="reset">Nova Comparação</b-button>
        </div>

        <div class="comparar-warning-banner">
          <b-icon icon="exclamation-triangle-fill" class="mr-2" />
          <div>
            <strong>{{ report.totalDiffs }} diferença(s) encontrada(s)</strong>
            <p class="mb-0 mt-1" style="font-size:0.85em">Diferenças esperadas — modelos de naturezas distintas.</p>
          </div>
        </div>

        <div v-for="section in sections" :key="section.key" class="comparar-section">
          <button class="comparar-section-header" @click="expandedSection = expandedSection === section.key ? null : section.key">
            <span>{{ section.title }}</span>
            <b-icon :icon="expandedSection === section.key ? 'chevron-up' : 'chevron-down'" />
          </button>
          <div v-if="expandedSection === section.key" class="comparar-section-body">

            <!-- Resumo -->
            <template v-if="section.key === 'resumo'">
              <div class="resumo-grid">
                <div class="resumo-card">
                  <div class="resumo-card-title">Operações Ausentes</div>
                  <div class="resumo-row"><span>PAP → RTO</span><span class="badge-red">{{ report.opsOnlyPAP.length }}</span></div>
                  <div class="resumo-row"><span>RTO → PAP</span><span class="badge-gray">{{ report.opsOnlyRTO.length }}</span></div>
                </div>
                <div class="resumo-card">
                  <div class="resumo-card-title">Correntes Ausentes</div>
                  <div class="resumo-row"><span>PAP → RTO</span><span class="badge-red">{{ report.streamsOnlyPAP.length }}</span></div>
                  <div class="resumo-row"><span>RTO → PAP</span><span class="badge-blue">{{ report.streamsOnlyRTO.length }}</span></div>
                </div>
              </div>
              <div class="resumo-footer-row">
                <span>Correntes com Origem/Destino Diferentes</span>
                <span class="badge-amber">{{ report.streamsDiff.length }}</span>
              </div>
              <div class="resumo-nota">
                <strong>Nota:</strong> {{ report.labelPAP }} (PAP) vs {{ report.labelRTO }} (RTO). Exclusões aplicadas: {{ report.exclusoesAplicadas }}.
              </div>
            </template>

            <!-- Operações ausentes -->
            <template v-else-if="section.key === 'ops'">
              <div class="section-search-wrap">
                <b-icon icon="search" class="section-search-icon" />
                <input v-model="sectionSearch.ops" class="section-search-input" placeholder="Filtrar operações..." />
                <button v-if="sectionSearch.ops" class="section-search-clear" @click="sectionSearch.ops = ''">
                  <b-icon icon="x" />
                </button>
              </div>
              <p class="section-subtitle">Em PAP, ausentes em RTO — {{ filteredOpsOnlyPAP.length }} de {{ report.opsOnlyPAP.length }}</p>
              <div class="item-list">
                <span v-for="op in filteredOpsOnlyPAP" :key="op" class="item-badge item-badge-red">{{ op }}</span>
                <span v-if="!filteredOpsOnlyPAP.length" class="text-muted">(nenhuma)</span>
              </div>
              <p class="section-subtitle mt-3">Em RTO, ausentes em PAP — {{ filteredOpsOnlyRTO.length }} de {{ report.opsOnlyRTO.length }}</p>
              <div class="item-list">
                <span v-for="op in filteredOpsOnlyRTO" :key="op" class="item-badge item-badge-gray">{{ op }}</span>
                <span v-if="!filteredOpsOnlyRTO.length" class="text-muted">(nenhuma)</span>
              </div>
            </template>

            <!-- Correntes ausentes -->
            <template v-else-if="section.key === 'correntes'">
              <div class="section-search-wrap">
                <b-icon icon="search" class="section-search-icon" />
                <input v-model="sectionSearch.correntes" class="section-search-input" placeholder="Filtrar correntes..." />
                <button v-if="sectionSearch.correntes" class="section-search-clear" @click="sectionSearch.correntes = ''">
                  <b-icon icon="x" />
                </button>
              </div>
              <p class="section-subtitle">Em PAP, ausentes em RTO — {{ filteredStreamsOnlyPAP.length }} de {{ report.streamsOnlyPAP.length }}</p>
              <div class="item-list">
                <span v-for="s in filteredStreamsOnlyPAP" :key="s" class="item-badge item-badge-red">{{ s }}</span>
                <span v-if="!filteredStreamsOnlyPAP.length" class="text-muted">(nenhuma)</span>
              </div>
              <p class="section-subtitle mt-3">Em RTO, ausentes em PAP — {{ filteredStreamsOnlyRTO.length }} de {{ report.streamsOnlyRTO.length }}</p>
              <div class="item-list">
                <span v-for="s in filteredStreamsOnlyRTO" :key="s" class="item-badge item-badge-blue">{{ s }}</span>
                <span v-if="!filteredStreamsOnlyRTO.length" class="text-muted">(nenhuma)</span>
              </div>
            </template>

            <!-- Correntes com diferença -->
            <template v-else-if="section.key === 'diff'">
              <div class="section-search-wrap">
                <b-icon icon="search" class="section-search-icon" />
                <input v-model="sectionSearch.diff" class="section-search-input" placeholder="Filtrar correntes..." />
                <button v-if="sectionSearch.diff" class="section-search-clear" @click="sectionSearch.diff = ''">
                  <b-icon icon="x" />
                </button>
              </div>
              <div v-for="item in filteredStreamsDiff" :key="item.name" class="diff-item">
                <div class="diff-item-name">{{ item.name }}</div>
                <div v-for="d in item.diffs" :key="d.attr" class="diff-attr">
                  <span class="diff-attr-label">{{ d.attr }}:</span>
                  <div class="diff-row"><span class="diff-source">PAP</span><span class="diff-value">{{ d.valPAP || '(não mapeada)' }}</span></div>
                  <div class="diff-row"><span class="diff-source">RTO</span><span class="diff-value">{{ d.valRTO || '(não mapeada)' }}</span></div>
                </div>
              </div>
              <span v-if="!filteredStreamsDiff.length" class="text-muted">(nenhuma)</span>
            </template>

          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { showError } from "@/global";

const EXCLUSION_PREFIXES = ['X', 'BlocoIndicadores', 'BUF_', 'M_120', 'M_130', 'ECT_130'];

export default {
  name: "CompararModelosXML",

  data() {
    return {
      slots: [
        { key: 'PAP', label: 'PAP', file: null, error: null },
        { key: 'RTO', label: 'RTO', file: null, error: null },
      ],
      loading: false,
      report: null,
      expandedSection: 'resumo',
      sectionSearch: { ops: '', correntes: '', diff: '' },
      sections: [
        { key: 'resumo',    title: 'Resumo da Comparação' },
        { key: 'ops',       title: 'Operações Ausentes' },
        { key: 'correntes', title: 'Correntes Ausentes' },
        { key: 'diff',      title: 'Correntes com Origem/Destino Diferentes' },
      ],
    };
  },

  computed: {
    filteredOpsOnlyPAP() {
      const q = this.sectionSearch.ops.toLowerCase();
      return q ? (this.report?.opsOnlyPAP || []).filter(o => o.toLowerCase().includes(q)) : (this.report?.opsOnlyPAP || []);
    },
    filteredOpsOnlyRTO() {
      const q = this.sectionSearch.ops.toLowerCase();
      return q ? (this.report?.opsOnlyRTO || []).filter(o => o.toLowerCase().includes(q)) : (this.report?.opsOnlyRTO || []);
    },
    filteredStreamsOnlyPAP() {
      const q = this.sectionSearch.correntes.toLowerCase();
      return q ? (this.report?.streamsOnlyPAP || []).filter(s => s.toLowerCase().includes(q)) : (this.report?.streamsOnlyPAP || []);
    },
    filteredStreamsOnlyRTO() {
      const q = this.sectionSearch.correntes.toLowerCase();
      return q ? (this.report?.streamsOnlyRTO || []).filter(s => s.toLowerCase().includes(q)) : (this.report?.streamsOnlyRTO || []);
    },
    filteredStreamsDiff() {
      const q = this.sectionSearch.diff.toLowerCase();
      return q ? (this.report?.streamsDiff || []).filter(i => i.name.toLowerCase().includes(q)) : (this.report?.streamsDiff || []);
    },
  },

  methods: {
    abrir() {
      this.reset();
      this.$bvModal.show('modalCompararModelos');
    },

    abrirComStrings({ xmlPAP, xmlRTO, labelPAP = 'PAP', labelRTO = 'RTO' }) {
      this.reset();
      this.slots[0].file = { name: labelPAP };
      this.slots[1].file = { name: labelRTO };
      this.$bvModal.show('modalCompararModelos');
      this.$nextTick(() => {
        this.executarComStrings(xmlPAP, xmlRTO, labelPAP, labelRTO);
      });
    },

    reset() {
      this.slots = [
        { key: 'PAP', label: 'PAP', file: null, error: null },
        { key: 'RTO', label: 'RTO', file: null, error: null },
      ];
      this.report = null;
      this.expandedSection = 'resumo';
      this.sectionSearch = { ops: '', correntes: '', diff: '' };
      this.sections[1].title = 'Operações Ausentes';
      this.sections[2].title = 'Correntes Ausentes';
      this.sections[3].title = 'Correntes com Origem/Destino Diferentes';
    },

    onFile(event, key) {
      const file = event.target.files[0];
      if (!file) return;
      const slot = this.slots.find(s => s.key === key);
      if (!slot) return;

      const isXml = file.name.toLowerCase().endsWith('.xml') ||
                    file.type === 'text/xml' ||
                    file.type === 'application/xml';

      if (!isXml) {
        slot.file = null;
        this.$set(slot, 'error', `"${file.name}" não é um arquivo XML válido.`);
        event.target.value = '';
        return;
      }

      slot.file = file;
      this.$set(slot, 'error', null);
    },

    executar() {
      const slotPAP = this.slots[0];
      const slotRTO = this.slots[1];
      if (!slotPAP.file || !slotRTO.file) return;

      this.loading = true;

      const readFile = file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file, 'UTF-8');
      });

      Promise.all([readFile(slotPAP.file), readFile(slotRTO.file)])
        .then(([xmlPAP, xmlRTO]) => {
          this.executarComStrings(xmlPAP, xmlRTO, slotPAP.file.name, slotRTO.file.name);
        })
        .catch(() => showError('Erro ao ler os arquivos XML. Verifique se são arquivos válidos.'))
        .finally(() => { this.loading = false; });
    },

    executarComStrings(xmlPAP, xmlRTO, labelPAP = 'PAP', labelRTO = 'RTO') {
      try {
        const { ops: opsPAP, streams: streamsPAP } = this.parseModel(xmlPAP);
        const { ops: opsRTO, streams: streamsRTO } = this.parseModel(xmlRTO);

        const opsOnlyPAP = Object.keys(opsPAP).filter(k => !opsRTO[k]).sort();
        const opsOnlyRTO = Object.keys(opsRTO).filter(k => !opsPAP[k]).sort();

        const streamKeysPAP = new Set(Object.keys(streamsPAP));
        const streamKeysRTO = new Set(Object.keys(streamsRTO));
        const streamsOnlyPAP = [...streamKeysPAP].filter(k => !streamKeysRTO.has(k)).sort();
        const streamsOnlyRTO = [...streamKeysRTO].filter(k => !streamKeysPAP.has(k)).sort();

        const streamsDiff = [];
        for (const sid of [...streamKeysPAP].filter(k => streamKeysRTO.has(k)).sort()) {
          const diffs = [];
          const sPAP = streamsPAP[sid];
          const sRTO = streamsRTO[sid];
          if (!this.setsEqual(sPAP.origens, sRTO.origens))
            diffs.push({ attr: 'Origem', valPAP: [...sPAP.origens].sort().join(', '), valRTO: [...sRTO.origens].sort().join(', ') });
          if (!this.setsEqual(sPAP.destinos, sRTO.destinos))
            diffs.push({ attr: 'Destino', valPAP: [...sPAP.destinos].sort().join(', '), valRTO: [...sRTO.destinos].sort().join(', ') });
          if (diffs.length) streamsDiff.push({ name: sid, diffs });
        }

        this.report = {
          totalDiffs: opsOnlyPAP.length + opsOnlyRTO.length + streamsOnlyPAP.length + streamsOnlyRTO.length + streamsDiff.length,
          opsOnlyPAP, opsOnlyRTO, streamsOnlyPAP, streamsOnlyRTO, streamsDiff,
          labelPAP, labelRTO,
          exclusoesAplicadas: EXCLUSION_PREFIXES.join(', '),
        };

        this.sections[1].title = `Operações Ausentes (${opsOnlyPAP.length} PAP→RTO, ${opsOnlyRTO.length} RTO→PAP)`;
        this.sections[2].title = `Correntes Ausentes (${streamsOnlyPAP.length} PAP→RTO, ${streamsOnlyRTO.length} RTO→PAP)`;
        this.sections[3].title = `Correntes com Origem/Destino Diferentes (${streamsDiff.length})`;
      } catch (e) {
        console.error('Erro ao comparar modelos XML:', e);
        showError('Erro ao comparar modelos XML.');
      }
    },

    parseModel(xmlString) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlString, 'application/xml');
      const ops = {};
      const streams = {};

      const operacoesNode = doc.querySelector('Operacoes');
      if (operacoesNode) {
        for (const op of operacoesNode.children) {
          const name = (op.getAttribute('name') || '').trim();
          if (name && !EXCLUSION_PREFIXES.some(p => name.startsWith(p)))
            ops[name] = op.tagName;
        }
      }

      const correntesNode = doc.querySelector('Correntes');
      if (correntesNode) {
        for (const c of correntesNode.children) {
          const name = (c.getAttribute('name') || '').trim();
          if (name) streams[name] = { origens: new Set(), destinos: new Set() };
        }
      }

      if (operacoesNode) {
        for (const op of operacoesNode.children) {
          const opName = (op.getAttribute('name') || '').trim();
          for (const saida of op.querySelectorAll('Saida')) {
            const n = (saida.getAttribute('name') || '').trim();
            if (streams[n]) streams[n].origens.add(opName);
          }
          for (const entrada of op.querySelectorAll('Entrada')) {
            const n = (entrada.getAttribute('name') || '').trim();
            if (streams[n]) streams[n].destinos.add(opName);
          }
        }
      }

      return { ops, streams };
    },

    setsEqual(a, b) {
      return a.size === b.size && [...a].every(v => b.has(v));
    },
  },
};
</script>

<style scoped>
/* Header */
::v-deep .comparar-modelos-header { padding: 0; border-bottom: none; }

.comparar-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #1a1a1a;
  color: #fff;
  border-bottom: 3px solid #f37021;
  padding: 10px 16px;
  width: 100%;
}

.comparar-header-title {
  display: flex;
  align-items: center;
  font-weight: bold;
  font-size: 0.95rem;
  gap: 8px;
}

.comparar-header-icon { color: #f37021; }

.comparar-header-close {
  background: none;
  border: none;
  color: #aaa;
  font-size: 1.1rem;
  cursor: pointer;
  line-height: 1;
}
.comparar-header-close:hover { color: #fff; }

/* Body */
.comparar-body { min-height: 400px; }

/* Upload */
.comparar-upload-area { padding: 8px 0; }

.comparar-upload-intro { text-align: center; margin-bottom: 24px; }
.comparar-upload-title { font-size: 1.1rem; font-weight: bold; color: #333; margin-bottom: 4px; }
.comparar-upload-subtitle { font-size: 0.85rem; color: #888; margin: 0; }

.comparar-upload-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 28px;
}

.comparar-upload-card {
  border: 2px dashed #ccc;
  border-radius: 12px;
  padding: 28px 16px;
  text-align: center;
  background: #f9f9f9;
  transition: border-color 0.2s, background 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.comparar-upload-card.card-filled { border-color: #28a745; background: #f0fff4; }
.comparar-upload-card.card-error  { border-color: #dc3545; background: #fff5f5; }

.card-icon-ok    { color: #28a745; }
.card-icon-empty { color: #aaa; }
.card-icon-error { color: #dc3545; }
.card-label { font-weight: bold; color: #555; font-size: 0.9rem; margin: 0; }
.card-filename { font-size: 0.78rem; color: #777; margin: 0; word-break: break-all; }
.card-filename-error { color: #dc3545; font-weight: 600; }

.comparar-upload-action { display: flex; justify-content: center; }

.btn-comparar {
  background: #f37021;
  border-color: #f37021;
  color: #fff;
  font-weight: bold;
  padding: 10px 32px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}
.btn-comparar:hover:not(:disabled) { background: #d45f10; border-color: #d45f10; }
.btn-comparar:disabled { opacity: 0.5; cursor: not-allowed; }

/* Relatório */
.comparar-report-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.comparar-report-title { font-size: 1.1rem; font-weight: bold; margin: 0; }
.btn-nova-comparacao { color: #f37021; font-size: 0.85rem; padding: 0; }
.btn-nova-comparacao:hover { color: #d45f10; }

.comparar-warning-banner {
  background: #fffbeb;
  border-left: 4px solid #f59e0b;
  border-radius: 0 4px 4px 0;
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  color: #92400e;
  margin-bottom: 16px;
  font-size: 0.88rem;
}

/* Accordion */
.comparar-section { border: 1px solid #e5e7eb; border-radius: 6px; margin-bottom: 8px; overflow: hidden; }

.comparar-section-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  border: none;
  cursor: pointer;
  font-size: 0.87rem;
  font-weight: 600;
  color: #374151;
  text-align: left;
}
.comparar-section-header:hover { background: #f3f4f6; }

.comparar-section-body { padding: 16px; border-top: 1px solid #e5e7eb; }

/* Resumo */
.resumo-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }

.resumo-card { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; }
.resumo-card-title { font-size: 0.72rem; font-weight: bold; text-transform: uppercase; color: #6b7280; margin-bottom: 8px; }
.resumo-row { display: flex; justify-content: space-between; align-items: center; font-size: 0.83rem; color: #555; margin-top: 6px; }

.resumo-footer-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

.resumo-nota { background: #eff6ff; border: 1px solid #dbeafe; border-radius: 4px; padding: 10px 12px; font-size: 0.78rem; color: #1d4ed8; }

.badge-red   { background: #fee2e2; color: #ef4444; font-weight: bold; border-radius: 4px; padding: 2px 8px; font-family: monospace; }
.badge-gray  { background: #f3f4f6; color: #6b7280; font-weight: bold; border-radius: 4px; padding: 2px 8px; font-family: monospace; }
.badge-blue  { background: #dbeafe; color: #3b82f6; font-weight: bold; border-radius: 4px; padding: 2px 8px; font-family: monospace; }
.badge-amber { background: #fef3c7; color: #f59e0b; font-weight: bold; border-radius: 4px; padding: 2px 8px; font-family: monospace; }

/* Listas */
.section-subtitle { font-size: 0.82rem; font-weight: 600; color: #555; margin-bottom: 8px; }

/* --- Search bar --- */
.section-search-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 10px;
  margin-bottom: 14px;
  transition: border-color 180ms ease, box-shadow 180ms ease;
}

.section-search-wrap:focus-within {
  border-color: #ff5f00;
  box-shadow: 0 0 0 3px rgba(255, 95, 0, 0.12);
  background-color: #ffffff;
}

.section-search-icon {
  color: #9ca3af;
  font-size: 0.8rem;
  flex-shrink: 0;
  transition: color 180ms ease;
}

.section-search-wrap:focus-within .section-search-icon {
  color: #ff5f00;
}

.section-search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.82rem;
  color: #374151;
  outline: none;
}

.section-search-input::placeholder {
  color: #9ca3af;
}

.section-search-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  transition: background-color 150ms, color 150ms;
}

.section-search-clear:hover {
  background-color: #e5e7eb;
  color: #374151;
}
.item-list { display: flex; flex-wrap: wrap; gap: 6px; }
.item-badge { font-size: 0.75rem; padding: 2px 8px; border-radius: 4px; font-family: monospace; }
.item-badge-red  { background: #fee2e2; color: #b91c1c; }
.item-badge-gray { background: #f3f4f6; color: #374151; }
.item-badge-blue { background: #dbeafe; color: #1d4ed8; }

/* Diff */
.diff-item { border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; margin-bottom: 10px; }
.diff-item-name { font-weight: bold; font-size: 0.88rem; color: #1f2937; margin-bottom: 8px; }
.diff-attr { margin-bottom: 6px; }
.diff-attr-label { font-size: 0.78rem; font-weight: 600; color: #6b7280; display: block; margin-bottom: 2px; }
.diff-row { display: flex; align-items: baseline; gap: 8px; font-size: 0.8rem; margin-bottom: 2px; }
.diff-source { background: #f3f4f6; color: #374151; font-weight: bold; font-size: 0.7rem; padding: 1px 6px; border-radius: 3px; min-width: 30px; text-align: center; }
.diff-value { color: #555; word-break: break-word; }
</style>
